<template>
  <v-container data-testid="order-detail-page" class="mt-5">
    <v-card class="pa-4" elevation="1">
      <div class="mb-3">
        <router-link
          :to="ordersListPath"
          class="text-body-2"
          data-testid="order-detail-back"
        >
          &larr; Back to orders
        </router-link>
      </div>

      <!--
        A closed or archived store, no emailed code, and NOT a signed-in
        shopper (OD-20261016-3, "B"): the order-link form and nothing else --
        no request is made (router/ownOrdersSession.js).
      -->
      <div
        v-if="lookupOnly"
        class="d-flex justify-center pa-6"
        data-testid="order-detail-lookup-only"
      >
        <StorefrontGuestOrderLookup />
      </div>

      <div
        v-else-if="loading"
        class="d-flex align-center justify-center pa-6"
        data-testid="order-detail-loading"
      >
        <v-progress-circular indeterminate color="primary" class="mr-3" />
        <span>Loading your order&hellip;</span>
      </div>

      <div
        v-else-if="loadError"
        class="text-center pa-6"
        data-testid="order-detail-error"
      >
        <v-icon size="48" color="error" icon="error_outline" class="mb-2" />
        <div class="text-body-1">
          We couldn't load this order. Please try again later.
        </div>
      </div>

      <div
        v-else-if="!order || !isOwn"
        class="text-center pa-6"
        data-testid="order-detail-not-found"
      >
        <v-icon size="48" color="grey" icon="search_off" class="mb-2" />
        <div class="text-body-1">
          We couldn't find that order.
        </div>
      </div>

      <template v-else>
        <div data-testid="order-detail-header" class="mb-4">
          <!--
            The STORE's order number, not `$route.params.id`. This rendered
            the ROUTING KEY straight into the page title, so the heading
            read "Order #8" for order AC-2026-01001 and never consulted the
            order at all. The route still keys on the id; only the heading
            changes. Falls back to a bare "Order" rather than the id when
            the number is not yet assigned.
          -->
          <v-card-title
            class="text-h5 pa-0 mb-1"
            data-testid="order-detail-order-number"
          >
            <!--
              "Order: AC-2026-01007". The separator lives OUTSIDE the v-if
              because a space inside a conditional span collapses when the
              span renders, which is how this shipped as "OrderAC-2026-01007".
            -->
            Order<template v-if="orderNumber">: {{ orderNumber }}</template>
          </v-card-title>
          <div class="text-body-2 text-grey-darken-1">
            Placed {{ formatDate(order.created_at) }}
          </div>
          <div class="d-flex align-center mt-2">
            <!--
              The raw enum used to reach the DOM here, so a shopper saw
              "awaiting_batch_charge" -- a word for an internal batching
              concept they have no model for. `statusLabel` maps it to
              plain language; see its comment for what each state MEANS.

              No "Total" here any more. `order.total` is the PRE-discount
              sub-total, and printing it in bold beside the status made it
              read as the amount charged: on a free-item order it said
              $578 while the card took $479, and the money block 20 lines
              below labelled the SAME value "Sub-total". One number, two
              contradictory labels, the wrong one prominent. The money
              block is now the single source of truth for money.
            -->
            <span
              class="text-body-1"
              data-testid="order-detail-status"
            >{{ statusLabel }}</span>
          </div>

          <!--
            The money block has to FOOT. Previously this page showed a
            single bare "Total: $432.00" with no discount and no charged
            amount, so on a store running the free-item rule there was
            nothing on screen explaining why the card was charged less --
            structurally the most opaque of these surfaces.

            Sub-total, then the discount when non-zero, then what was
            actually charged. Wording matches CartSummary.vue, the
            confirmation email and the confirmation page.
          -->
          <div
            v-if="freeItemDiscount"
            class="mt-3"
            data-testid="order-detail-money"
          >
            <div class="d-flex justify-space-between">
              <span class="text-body-2">Sub-total</span>
              <span class="text-body-2">{{ formatTotal(order.total) }}</span>
            </div>
            <div
              class="d-flex justify-space-between text-success"
              data-testid="order-detail-free-item-discount"
            >
              <span class="text-body-2">Free item</span>
              <span class="text-body-2">-{{ formatTotal(freeItemDiscount) }}</span>
            </div>
            <div class="d-flex justify-space-between font-weight-bold mt-1">
              <span class="text-body-1">Charged</span>
              <span
                class="text-body-1"
                data-testid="order-detail-charged"
              >{{ formatTotal(chargedAmount) }}</span>
            </div>
          </div>
          <!--
            Re-order needs a cart session. A guest arriving on a tokenized
            order link (card tmMqkxM1) has none, and the token grants READ of
            this one order only -- so the action is hidden rather than
            offered-then-failing. The same holds for a store that is closed or
            archived (no store session): nothing can be added to its cart.
          -->
          <div v-if="!guestMode && !ownOrdersOnly" class="mt-3">
            <v-btn
              color="primary"
              :disabled="reordering || orderItems.length === 0"
              :loading="reordering"
              data-testid="order-detail-reorder"
              @click="reorderAllItems"
            >
              <template v-if="reordering">
                <v-progress-circular
                  indeterminate
                  size="20"
                  width="2"
                  class="mr-2"
                  data-testid="order-detail-reorder-spinner"
                />
                Adding to cart&hellip;
              </template>
              <template v-else>
                Re-order all items
              </template>
            </v-btn>
          </div>
        </div>

        <v-table
          density="comfortable"
          data-testid="order-detail-items"
          class="mb-4"
        >
          <thead>
            <tr>
              <th class="text-left">Item</th>
              <th class="text-left">Primary</th>
              <th class="text-left">Secondary</th>
              <th class="text-right">Qty</th>
              <th class="text-right">Unit Price</th>
              <th class="text-right">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="orderItems.length === 0">
              <td colspan="6" class="text-center text-grey-darken-1">
                No items on this order.
              </td>
            </tr>
            <tr
              v-for="item in orderItems"
              :key="item._jv ? item._jv.id : item.id"
              data-testid="order-detail-item-row"
            >
              <td>{{ itemName(item) }}</td>
              <td>{{ item.primary_attribute_value || "—" }}</td>
              <td>{{ item.secondary_attribute_value || "—" }}</td>
              <td class="text-right">{{ formatQuantity(item.quantity) }}</td>
              <td class="text-right">{{ formatItemPrice(item.price) }}</td>
              <td class="text-right">{{ computeLineSubtotal(item) }}</td>
            </tr>
          </tbody>
        </v-table>

        <!--
          Where a store-PRESET ship-to goes (card #402): the server's
          "Ships to <name, address>, set by <store>" sentence. It already
          states the address, so the plain "Shipping Address" block below
          steps aside for it; a shopper-entered address keeps that block.
        -->
        <order-ship-to-notice
          :ship-to="shipToNotice"
          testid-prefix="order-detail-ship-to"
          class="mb-2"
        />
        <div v-if="shippingAddress && !shipToNotice.disclosure" class="mb-2">
          <div class="text-subtitle-2 text-grey-darken-1">
            Shipping Address
          </div>
          <div class="text-body-1">{{ shippingAddress }}</div>
        </div>

        <!--
          Tracking, rendered by the same component as the portal's order
          view. `shipments` stays null until a read succeeds, and null
          renders nothing -- a failed tracking read must not tell a shopper
          their order has not shipped.
        -->
        <OrderShipments :shipments="shipments" class="mb-2" />
      </template>
    </v-card>
  </v-container>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import {
  formatOrderDate,
  formatOrderTotal,
  getOrderNumber,
  getOrderChargedAmount,
} from "./orderListHelpers";
// Shared with StorefrontOrdersList, which had the SAME raw-enum defect on
// the screen a shopper reaches first. One map, both surfaces -- two copies
// would drift, which is exactly how the free-item discount reached six
// surfaces before it was consolidated.
import { shopperOrderStatus } from "./orderStatusCopy";
import {
  computeLineSubtotal,
  extractShippingAddress,
  formatItemPrice,
  isOwnOrder,
  guestOrderRequest,
  guestOrderMatchesRequest,
} from "./orderDetailHelpers";
import {
  buildReorderArgs,
  mergedQuantityForCart,
  reorderLogoChoice,
  reorderLogoMessage,
  summarizeReorderResult,
} from "./reorderHelpers";
// PR-R5: storefront URL restructure — `/orders` -> `/store/:slug/orders`,
// `/cart` -> `/store/:slug/cart`.
import { resolveStorefrontSlug } from "@/router/storefrontSlug";
import { STOREFRONT_STORE_ID, cartStoreArgs } from "@/router/storefrontStoreId";
import { fetchStoreCart } from "./storeCart";
import { formatQuantity } from "@/utils/formatQuantity";
import OrderShipments from "@/components/Order/OrderShipments.vue";
import OrderShipToNotice from "@/components/Order/OrderShipToNotice.vue";
import { fetchOrderShipTo } from "@/components/Order/orderShipTo";
import StorefrontGuestOrderLookup from "./StorefrontGuestOrderLookup.vue";
import {
  STOREFRONT_NO_STORE_SESSION,
  ownOrdersPageMode,
  personalShopperAccountId,
} from "@/router/ownOrdersSession";

export default {
  name: "StorefrontOrderDetail",
  components: { OrderShipments, OrderShipToNotice, StorefrontGuestOrderLookup },
  inject: {
    // The store the page is for: the id StorefrontSessionGate resolved from
    // the URL slug (router/storefrontStoreId.js). Re-order names it, so the
    // server adds to THIS store and refuses a product of another.
    gateStoreId: { from: STOREFRONT_STORE_ID, default: null },
    // True when the gate rendered this page with no store session (a closed
    // or archived store; router/ownOrdersSession.js).
    noStoreSession: { from: STOREFRONT_NO_STORE_SESSION, default: false },
  },

  data() {
    return {
      order: null,
      orderItems: [],
      loading: false,
      loadError: false,
      reordering: false,
      // OD-20261016-3: the store is closed or archived (no store session).
      // `lookupOnly` -- not signed in: the order-link form only.
      // `ownOrdersOnly` -- a signed-in shopper reading their own order;
      // `personalAccountId` is who they are when no account is in memory.
      lookupOnly: false,
      ownOrdersOnly: false,
      personalAccountId: null,
      // True when this page was opened from an emailed order link (card
      // tmMqkxM1). The backend authorized the read on the TOKEN, which is
      // scoped to exactly this one order, so the account-match gate below
      // does not apply -- a guest has no account to match.
      guestMode: false,
      // OrderShipment.to_public_dict() rows, or null while unknown. See the
      // template: [] and null are different claims.
      shipments: null,
      // GET /orders/<id>/ship-to for a signed-in shopper's order, or null.
      shipTo: null,
    };
  },
  computed: {
    isOwn() {
      // Ownership is not expressible for a guest -- they have no account to
      // match. The equivalent second layer for that path is ORDER identity,
      // enforced in load() via guestOrderMatchesRequest BEFORE the order is
      // ever assigned; a mismatch leaves this.order null, so reaching here
      // with a non-null order means that check already passed.
      if (this.guestMode) {
        return this.order != null;
      }
      const myAccountId =
        this.$store.state.account?._jv?.id ??
        (this.ownOrdersOnly ? this.personalAccountId : null);
      return isOwnOrder(this.order, myAccountId);
    },
    shippingAddress() {
      return extractShippingAddress(this.order);
    },
    /**
     * What the shopper is told about a PRESET ship-to: the server's meaning
     * line and "Ships to ..., set by <store>" sentence. A guest's come on the
     * tokenized payload; a signed-in shopper's from the order's ship-to
     * route. Never composed here.
     */
    shipToNotice() {
      if (this.guestMode) {
        return {
          meaning: this.order?.ship_to_meaning || null,
          disclosure: this.order?.ship_to_disclosure || null,
        };
      }
      return {
        meaning: this.shipTo?.meaning || null,
        disclosure: this.shipTo?.disclosure || null,
      };
    },
    /**
     * The STORE's order number ("AC-2026-01001"), read from the ORDER --
     * never from the URL. Covers both load paths: the authenticated fetch
     * and the tokenized guest payload, which also carries `order_number`.
     * Null when unassigned, so the heading degrades to a bare "Order"
     * rather than reintroducing the internal id.
     */
    orderNumber() {
      return getOrderNumber(this.order);
    },
    /**
     * Plain-language order state. NEVER the raw enum.
     *
     * The shopper saw `awaiting_batch_charge` on this page (Jake,
     * 2026-09-12: "the shopper probably has no idea what a batch is").
     * Batching is OUR concept -- a distributor collects orders and charges
     * them together -- and a shopper has no model for it.
     *
     * READ THIS BEFORE "CORRECTING" THE `processing` ROW. `processing`
     * means the CARD WAS CHARGED, not that the decorator started work: it
     * is set the moment the charge succeeds (`batch_charge.py`
     * CHARGED_STATUS). Labelling it "Processing" to a shopper would
     * assert production has begun, which is false and unfixable later --
     * they will have read it.
     *
     * Only states we can OBSERVE appear here. The decorator's own
     * fulfilment status (`production`, `shipped`) arrives on the
     * tf-deconetwork webhook into `order_fulfillment_events`, but nothing
     * serializes it to this page yet, and their taxonomy is
     * per-workspace editable -- so we would be rendering a key we neither
     * control nor can enumerate. Deliberately absent rather than guessed.
     *
     * Unknown states fall through to a neutral label rather than leaking
     * the enum: a new status added server-side must not put an
     * underscore in front of a shopper.
     */
    statusLabel() {
      return shopperOrderStatus(this.order?.status, this.order?.payment_state);
    },
    /**
     * Total free-item discount frozen on this order's lines, in DOLLARS.
     *
     * Sums the persisted per-line ``free_item_discount_cents`` and NEVER
     * re-derives the rule -- the same source and reasoning as
     * ``models/account.py::_frozen_free_item_discount_cents``. An order's
     * charge must not change because the store's configuration did.
     *
     * UNITS: the column is integer CENTS while the order money columns are
     * Float DOLLARS. The division happens here, exactly once.
     *
     * Returns 0 when nothing is discounted, so the money block hides
     * rather than printing "-$0.00" for the stores that do not run the
     * rule -- which is every store except AdventHealth's. Covers BOTH load
     * paths: `orderItems` is populated from jsonapi-vuex for an
     * authenticated shopper and from `guestOrder.items` for a tokenized
     * guest link.
     */
    freeItemDiscount() {
      const items = Array.isArray(this.orderItems) ? this.orderItems : [];
      const cents = items.reduce(
        (sum, item) => sum + (Number(item?.free_item_discount_cents) || 0),
        0
      );
      return cents > 0 ? cents / 100 : 0;
    },
    /**
     * What the shopper was actually charged. Prefers the frozen
     * ``charged_amount`` and falls back to the pre-discount total when the
     * column is absent (nullable; legacy rows pre-date it), since "$0.00"
     * for a real order would be a worse lie.
     */
    chargedAmount() {
      return getOrderChargedAmount(this.order);
    },
    // Convergence (card q7EQ3a0b): orders moved to /shop/:slug/orders.
    // Back-to-list link uses the resolved slug; falls back to /orders if no
    // slug (REDIRECTS catches it).
    ordersListPath() {
      const slug = resolveStorefrontSlug(this.$route);
      return slug ? `/shop/${slug}/orders` : "/orders";
    },
  },
  async created() {
    await this.load();
  },
  methods: {
    // Template access to the shared helper: the Qty column reads "x5".
    formatQuantity,
    formatDate(iso) {
      return formatOrderDate(iso);
    },
    formatTotal(amount) {
      return formatOrderTotal(amount);
    },
    formatItemPrice(price) {
      return formatItemPrice(price);
    },
    computeLineSubtotal(item) {
      return computeLineSubtotal(item);
    },
    itemName(item) {
      // campaign_product is included via the JSON:API include param. Guard
      // against an empty relationship object (project_empty-relationship-objects.md).
      const product = item && item.campaign_product;
      if (!product || !product.product_name) return "Item";
      return product.product_name;
    },
    async reorderAllItems() {
      // Loops the (already-loaded) orderItems and POSTs each to the
      // canonical /accounts/<id>/update_cart_quantity endpoint. Per-item
      // errors raise a toast and we CONTINUE through the rest — partial
      // success is preferable to bailing on the first stale variant.
      if (this.reordering) return;
      const accountId = this.$store.state.account?._jv?.id;
      if (accountId == null) {
        this.$store.dispatch(
          "raiseError",
          "We couldn't find your account to add items to your cart.",
        );
        return;
      }

      this.reordering = true;
      const results = [];
      // Each product's per-color logos, fetched once per product however
      // many of its lines the order holds.
      const slug = resolveStorefrontSlug(this.$route);
      const logosByProduct = new Map();
      try {
        // The merge base: THIS store's cart as the server holds it now
        // (`store_cart`; the account carries no cart). Kept current from
        // each write's response below. A failed read re-orders nothing: an
        // absolute write on a guessed base could clobber a line.
        let currentCart;
        try {
          currentCart = await fetchStoreCart(
            this.$axios,
            accountId,
            this.gateStoreId,
          );
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("Re-order could not read the cart", describeError(err));
          this.$store.dispatch(
            "raiseError",
            "We couldn't read your cart, so nothing was re-ordered. Try again.",
          );
          return;
        }
        for (const item of this.orderItems) {
          const args = buildReorderArgs(item);
          const name = this.itemName(item);
          if (!args) {
            this.$store.dispatch(
              "raiseError",
              `Could not re-order ${name}: missing product info`,
            );
            results.push({ success: false, name, error: "invalid-item" });
            continue;
          }
          try {
            // The logo is part of the line: re-add the one the order was
            // made with, checked against what the product offers today
            // (reorderLogoChoice). A line that cannot keep its logo is
            // skipped with a reason rather than added undecorated.
            const cpKey = String(args.campaign_product_id);
            if (!logosByProduct.has(cpKey)) {
              const productResponse = await this.$axios.get(
                `/storefront/${encodeURIComponent(slug)}/products/${args.campaign_product_id}`,
              );
              logosByProduct.set(cpKey, productResponse?.data?.logos || {});
            }
            const logo = reorderLogoChoice(item, logosByProduct.get(cpKey));
            if (!logo.ok) {
              this.$store.dispatch(
                "raiseError",
                reorderLogoMessage(name, logo.reason),
              );
              results.push({ success: false, name, error: logo.reason });
              continue;
            }
            // update_cart_quantity is ABSOLUTE: it sets the cart line to the
            // posted quantity, overwriting any existing value. Sum with the
            // matching cart line first so we don't silently clobber a
            // shopper's existing quantity for the same product/variant/logo.
            const mergedArgs = {
              ...args,
              ...cartStoreArgs(this.gateStoreId),
              quantity: mergedQuantityForCart(
                item,
                currentCart,
                logo.selectedArtworkId,
              ),
              // Omitted (not null) with no logo, as on the PDP, so an
              // undecorated line keeps the payload shape it always had.
              ...(logo.selectedArtworkId != null
                ? { selected_artwork_id: logo.selectedArtworkId }
                : {}),
            };
            const response = await this.$axios.post(
              `/accounts/${accountId}/update_cart_quantity`,
              {
                meta: {
                  method: "update_cart_quantity",
                  args: mergedArgs,
                },
              },
            );
            // Mirror ProductPage.vue: the server returns the new cart in
            // meta.result.cart (meta.result also carries message/lines);
            // keep the store in sync so the badge, popup cart, and the next
            // line's merge see the adds.
            const cart = response?.data?.meta?.result?.cart;
            if (Array.isArray(cart)) {
              currentCart = cart;
              this.$store.commit("setCart", cart);
            }
            results.push({ success: true, name });
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error("Re-order item failed", name, describeError(err));
            this.$store.dispatch(
              "raiseError",
              `Could not re-order ${name}`,
            );
            results.push({
              success: false,
              name,
              error: err?.message || "request-failed",
            });
          }
        }

        const summary = summarizeReorderResult(results);
        if (summary.successCount > 0) {
          this.$store.dispatch("raiseSuccess", summary.summaryMessage);
        } else if (summary.errorCount > 0) {
          this.$store.dispatch("raiseError", summary.summaryMessage);
        }
        if (summary.navigateToCart) {
          // Convergence (card q7EQ3a0b): cart moved to /shop/:slug/cart.
          this.$router.push(slug ? `/shop/${slug}/cart` : "/cart");
        }
      } finally {
        this.reordering = false;
      }
    },
    /**
     * Tracking for a signed-in shopper's order.
     *
     * The SAFRS order read does not carry shipments (`include=shipments`
     * 500s: OrderShipment is deliberately not a mounted resource), so this
     * asks the order's shipments route -- only ever AFTER load() has
     * confirmed the order is this shopper's. Best-effort: a failure leaves
     * `shipments` null, which renders nothing, and never fails the page the
     * shopper came for.
     */
    async loadShipments(id, config) {
      try {
        const url = `/orders/${id}/shipments`;
        const resp = await (config
          ? this.$axios.get(url, config)
          : this.$axios.get(url));
        const rows = resp?.data?.shipments;
        this.shipments = Array.isArray(rows) ? rows : null;
      } catch (err) {
        this.shipments = null;
        // eslint-disable-next-line no-console
        console.warn("Could not load tracking for order " + id, describeError(err));
      }
    },
    /** The order's ship-to sentence; best-effort like tracking. */
    async loadShipTo(id, config) {
      this.shipTo = await fetchOrderShipTo(this.$axios, id, config);
    },
    async load() {
      this.loading = true;
      this.loadError = false;
      this.order = null;
      this.orderItems = [];
      this.shipments = null;
      this.shipTo = null;
      try {
        const id = this.$route.params.id;

        // Emailed order link (card tmMqkxM1). Guest checkout is the DEFAULT,
        // so this page is often opened by someone with NO ACCOUNT. When the
        // link carries a token, read through the guest endpoint, which
        // authorizes on the token and returns exactly that one order.
        const req = guestOrderRequest(id, this.$route.query.token);
        if (req.isGuest) {
          this.guestMode = true;
          const resp = await this.$axios.get(req.url, { params: req.params });
          const guestOrder = resp?.data?.order || null;

          // Defense in depth on the ANONYMOUS path (CLAUDE.md Principle 5).
          // The token is scoped to exactly one order server-side, but this
          // page must not take that on faith: the order returned has to be
          // the order that was asked for. Without this the guest branch had
          // no second layer at all, which inverts the stated intent of the
          // ownership gate on the one path where nobody is authenticated.
          // Nuke it from component state entirely rather than gating in the
          // template -- a leaked payload left in $vm.order is visible in Vue
          // DevTools and heap inspection.
          if (!guestOrderMatchesRequest(guestOrder, id)) {
            // eslint-disable-next-line no-console
            console.warn(
              "Guest order /" + id + " did not match the requested id; suppressing display.",
            );
            this.order = null;
            this.orderItems = [];
            return;
          }

          this.order = guestOrder;
          this.orderItems = guestOrder?.items || [];
          // The guest serializer carries shipments on the order itself
          // (endpoints/guest_orders.py). A payload without the key stays
          // null -- unknown, not "nothing shipped".
          this.shipments = Array.isArray(guestOrder?.shipments)
            ? guestOrder.shipments
            : null;
          return;
        }

        // A closed or archived store (no store session, OD-20261016-3):
        // a signed-in shopper reads their own order; anyone else gets the
        // order-link form and no request is made.
        this.personalAccountId = personalShopperAccountId();
        const mode = ownOrdersPageMode({
          noStoreSession: this.noStoreSession,
          personalAccountId: this.personalAccountId,
        });
        if (mode === "lookup") {
          this.lookupOnly = true;
          return;
        }
        this.ownOrdersOnly = mode === "own-orders";
        // Its 401 (an expired session) is this page's: it shows "not found"
        // rather than logging out to a store home that no longer opens.
        const recovery = this.ownOrdersOnly ? { skipAuthRecovery: true } : {};

        // Fetch order + included relationships. Backend /api/orders/<id>
        // is scoped by account_id for Shoppers via RoleMixin._campaign_perms
        // (api/src/models/mixins/roles.py — fix landed in PR #964 / issue #963).
        const order = await this.$store.dispatch("jv/get", [
          `/orders/${id}`,
          {
            params: {
              // No store row on a closed or archived store's path: the page
              // never reads it, and it is not the shopper's to receive.
              include: this.ownOrdersOnly
                ? "account,order_items"
                : "account,campaign,order_items",
            },
            ...recovery,
          },
        ]);

        // Defense in depth (CLAUDE.md Principle 5). If the backend returns
        // an order for the wrong account (regression), nuke the order from
        // component state entirely — do NOT just rely on template gating,
        // which leaves the leaked payload visible in $vm.order / Vue
        // DevTools / heap inspection.
        const myAccountId =
          this.$store.state.account?._jv?.id ??
          (this.ownOrdersOnly ? this.personalAccountId : null);
        if (!isOwnOrder(order, myAccountId)) {
          // eslint-disable-next-line no-console
          console.warn(
            "Order /" + id + " did not match current account; suppressing display.",
          );
          this.order = null;
          this.orderItems = [];
          return;
        }
        this.order = order;

        // Fetch order items separately to pull in campaign_product (the
        // initial include returns only the order_items list, not their
        // nested campaign_product attrs).
        const orderItems = await this.$store.dispatch("jv/get", [
          `/orders/${id}/order_items`,
          { params: { include: "campaign_product" }, ...recovery },
        ]);
        this.orderItems = Object.values(orderItems || {});

        await this.loadShipments(id, this.ownOrdersOnly ? recovery : undefined);
        await this.loadShipTo(id, this.ownOrdersOnly ? recovery : undefined);
      } catch (err) {
        // 404 from the backend (e.g., wrong account, deleted order) lands
        // in the not-found branch by leaving this.order null. Other errors
        // surface as the error branch.
        const status = err?.response?.status;
        if (status === 404 || status === 403) {
          this.order = null;
        } else {
          this.loadError = true;
        }
        // eslint-disable-next-line no-console
        console.error("Failed to load order detail", describeError(err));
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
